/* KEEP THE VIDEO'S URL STABLE ACROSS RELOADS.
 *
 * Reported 14 Oct 2026 (testing): "after merging two sub-tasks the screen goes
 * back to the first task and sub-task of the video."
 *
 * The cause was not the merge. Every split and merge ends in a full reload of
 * the job document (the server owns the geometry — see useJob), and the
 * document carries `media_url`, a PRESIGNED S3 URL. A presigned URL is signed
 * at request time — X-Amz-Date is the second it was made — so every reload
 * returned a DIFFERENT string for the same video. React set it on the <video>,
 * and a new `src` restarts the media element: it pauses, reloads, and puts the
 * playhead back at 0. The playhead crossing into the sub-task at 0 s then
 * selected T1 · sub-task 1, which is what the tester saw. The earlier fix for
 * merge-while-playing (structural() in Workspace) was still doing its job; it
 * was undone a moment later by the video restarting underneath it.
 *
 * So: the same object keeps the URL it already has while that URL has comfortably
 * long to live. Only a URL close to expiring is swapped for the fresh one — a
 * restart then is the lesser evil against a 403 halfway through a video.
 */

/** Swap a still-valid URL only when it has less than this left. */
export const MIN_URL_LIFE_MS = 30 * 60 * 1000;

function parts(url: string): { base: string; query: URLSearchParams } {
  const [base, rest = ""] = url.split("#")[0].split("?");
  return { base, query: new URLSearchParams(rest) };
}

/** When a presigned URL stops working (ms since epoch), or null if the URL is
 *  not presigned (a plain /media/... path never expires). SigV4 carries
 *  X-Amz-Date (YYYYMMDDTHHMMSSZ) + X-Amz-Expires (seconds); SigV2 carries an
 *  absolute `Expires` (epoch seconds). */
export function presignedExpiry(url: string): number | null {
  const q = parts(url).query;
  const date = q.get("X-Amz-Date");
  const ttl = q.get("X-Amz-Expires");
  if (date && ttl) {
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date);
    if (!m) return null;
    const signed = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return signed + Number(ttl) * 1000;
  }
  const abs = q.get("Expires");
  if (abs && /^\d+$/.test(abs)) return Number(abs) * 1000;
  return null;
}

/** The URL the player should use after a reload: the one it already has when
 *  it points at the same object and is not about to expire, else the new one. */
export function stableMediaUrl(prev: string | null | undefined, next: string | null,
                               now: number = Date.now()): string | null {
  if (!prev || !next || prev === next) return next;
  if (parts(prev).base !== parts(next).base) return next;   // a different video
  const exp = presignedExpiry(prev);
  if (exp === null) return prev;                             // same path, never expires
  return exp - now > MIN_URL_LIFE_MS ? prev : next;
}

/** 26 Oct 2026 — the 360p playback copy. When the file the player is showing
 *  fails to load: the master to switch to, or null when there is nothing left
 *  to try (already on the master, or no copy was in use) and the player should
 *  say the video is missing. */
export function sourceAfterError(active: string | null,
                                 fallback: string | null | undefined): string | null {
  return fallback && active !== fallback ? fallback : null;
}
